import React, {useState, useEffect, Fragment} from 'react';

import useSearchStore from '../../_Global/SearchStore';

interface PaginationList {
    showNextPageLink: boolean;
    showPreviousPageLink: boolean;
    showFirstPageLink: boolean;
    showLastPageLink: boolean;
    navigationList: PaginationButton[];
    lastPageStartRank: number;
}

interface PaginationButton {
    label: string;
    showButton: boolean;
    isCurrentPage: boolean;
    startRank: number;
}

/**
 * The maximum number of pagination buttons to display.
 * Does not include the "current page", "first page" or "last page" buttons.
 */
const PAGINATION_BUTTON_COUNT = 2;

/**
 * Toggle the option to always display a link to the last page of results.
 */
const ALWAYS_SHOW_LAST_PAGE = true;

export default function Pagination() {
    const [paginationList, setPaginationList] = useState<PaginationList>({
        showNextPageLink: false,
        showPreviousPageLink: false,
        showFirstPageLink: false,
        showLastPageLink: false,
        navigationList: [],
        lastPageStartRank: 0
    });

    const funnelbackQueryParameters = useSearchStore((state) => state.funnelbackQueryParameters);

    const setFunnelbackQueryParameters = useSearchStore((state) => state.setFunnelbackQueryParameters);

    const resultsSummary = useSearchStore((state) => state.resultsSummary);

    const setQueueScrollToForm = useSearchStore((state) => state.setQueueScrollToForm);

    useEffect(() => {
        const navigationList: PaginationButton[] = [];
        const totalMatchingResults = resultsSummary['totalMatching'];
        let currentStartRank = 1; // Default start rank
        let resultsPerPage = 1; // Fallback results per page
        let showNextPageLink = false;
        let showPreviousPageLink = false;
        let showFirstPageLink = false;
        let showLastPageLink = false;
        let resultCounter = 0;
        let startRankMatchesCounter = false;
        let buttonIsVisible = false;
        let pageLabelCounter = 1;
        let currentPagePaginationIndex = 0;
        let lastPageStartRank = 0;

        if (funnelbackQueryParameters['start_rank'] !== undefined) {
            currentStartRank = parseInt(funnelbackQueryParameters['start_rank'], 10);
        }

        if (funnelbackQueryParameters['num_ranks'] !== undefined) {
            resultsPerPage = funnelbackQueryParameters['num_ranks'];
        }

        // Check if only one page of results
        if (totalMatchingResults <= resultsPerPage) {
            // Only one button to display (current page)
            navigationList.push({
                label: '1',
                showButton: true,
                isCurrentPage: true,
                startRank: 1,
            });
        } else {
            // Generate the configuration for result pagination buttons
            for (let i = currentStartRank; resultCounter < totalMatchingResults; ++i) {
                startRankMatchesCounter = false;
                buttonIsVisible = false;

                /**
                 * Check if the current button represents the
                 * current page of results.
                 */
                if (resultCounter + 1 === currentStartRank) {
                    startRankMatchesCounter = true;
                    buttonIsVisible = true;
                    currentPagePaginationIndex = pageLabelCounter - 1;
                }

                // Capture the button configuration
                navigationList.push({
                    label: pageLabelCounter.toString(),
                    showButton: buttonIsVisible,
                    isCurrentPage: startRankMatchesCounter,
                    startRank: resultCounter + 1,
                });

                // Increase the button label number
                pageLabelCounter++;

                // Increase the counter to move to the next result set
                resultCounter = resultCounter + resultsPerPage;
            }

            let availableButtonCount = PAGINATION_BUTTON_COUNT;
            let paginationButtonCount = 0;
            let navigationListItem;

            for (let k = 0, x = PAGINATION_BUTTON_COUNT; k < x; ++k) {
                paginationButtonCount++;

                if (availableButtonCount === 0) {
                    // Stop the loop here if there are no more pagination buttons to assign.
                    break;
                }

                // Attempt to capture the "previous" pagination button
                navigationListItem = navigationList[currentPagePaginationIndex - paginationButtonCount];

                if (navigationListItem !== undefined) {
                    // Update this button to be visible
                    navigationList[currentPagePaginationIndex - paginationButtonCount]['showButton'] = true;
                    availableButtonCount--;
                }

                if (availableButtonCount === 0) {
                    // Stop the loop here if there are no more pagination buttons to assign.
                    break;
                }

                // Attempt to capture the "next" pagination button
                navigationListItem = navigationList[currentPagePaginationIndex + paginationButtonCount];

                if (navigationListItem !== undefined) {
                    // Update this button to be visible
                    navigationList[currentPagePaginationIndex + paginationButtonCount]['showButton'] = true;
                    availableButtonCount--;
                }
            }
        }

        if (ALWAYS_SHOW_LAST_PAGE) {
            navigationList[navigationList.length - 1]['showButton'] = true;
        }

        /**
         * If prevStart is available, show previous page link
         */
        if (resultsSummary.prevStart) {
            showPreviousPageLink = true;
        }

        /**
         * If the button for the first rank of results isn't visible,
         * then toggle on the "return to first page" button.
         */
        if (!navigationList[0]['showButton']) {
            showFirstPageLink = true;
        }

        /**
         * If nextStart is available, show next page link
         */
        if (resultsSummary.nextStart) {
            showNextPageLink = true;
        }

        /**
         * If the button for the last rank of results isn't visible,
         * then toggle on the "skip to last page" button.
         */
        if (!navigationList[navigationList.length - 1]['showButton']) {
            showLastPageLink = true;
        }

        /**
         * Determine the start rank of the last page of results.
         *
         * Calculate the number of results excluding the last page
         * and then add 1 for the start rank of the last page.
         */
        if (funnelbackQueryParameters['num_ranks'] !== undefined) {
            const numberOfResultPages = totalMatchingResults / funnelbackQueryParameters['num_ranks'];
            const moduloResultsPerPage = totalMatchingResults % funnelbackQueryParameters['num_ranks'];

            if (numberOfResultPages > 1) {
                if (moduloResultsPerPage !== 0) {
                    /**
                     * Uneven number of total results.
                     * Round down the total pages and add 1
                     */
                    lastPageStartRank = (Math.floor(totalMatchingResults / funnelbackQueryParameters['num_ranks']) * 16) + 1
                } else {
                    /**
                     * Exact number of total results.
                     * Take the second last page and add 1.
                     */
                    lastPageStartRank = ((numberOfResultPages - 1) * funnelbackQueryParameters['num_ranks']) + 1;
                }
            } else {
                // Only one page of results
                lastPageStartRank = 1;
            }
        }

        // Update the state
        setPaginationList({
            showNextPageLink,
            showPreviousPageLink,
            showFirstPageLink,
            showLastPageLink,
            navigationList,
            lastPageStartRank
        });
    }, [resultsSummary]);

    /**
     * Respond to changes to the resources search input
     */
    const onPaginationButtonClick = (evt: React.MouseEvent<HTMLElement>) => {
        const eventTarget = evt.currentTarget as HTMLElement;
        let startRank = eventTarget.getAttribute('data-startrank');

        evt.preventDefault();

        if (startRank === null) {
            // Fallback to the first page if the attribute is missing
            startRank = '1';
        }

        // Scroll to the top of the form
        setQueueScrollToForm(true);

        setFunnelbackQueryParameters({
            start_rank: startRank,
        });
    };

    return (
        <nav className="pagination__container no-wysiwyg col-span-4 xs:col-span-12 xl:col-span-10 xl:col-start-2" aria-label="Search results pagination">
            <ol className="pagination__list justify-center" itemScope itemType="http://schema.org/BreadcrumbList">
                { paginationList.showPreviousPageLink ? (
                    <li className="pagination__list-item" itemProp="itemListElement" itemType="http://schema.org/ListItem">
                        <button
                            className="pagination__icon-link"
                            data-startrank={resultsSummary.prevStart}
                            itemProp="item"
                            onClick={onPaginationButtonClick}
                            role="button"
                            >
                            <span className="sr-only">Return to previous page</span>
                            <svg className=""><use xlinkHref="#chevron-down"></use></svg>
                        </button>
                        <meta itemProp="position" content={resultsSummary.prevStart?.toString()} />
                    </li>
                ) : null }

                { paginationList['navigationList'].map((navigationButton, buttonIndex, navigationList) => {
                    let showTruncation = false;

                    // If this button is the last in the list and the previous button was not set to be visible, insert an ellipsis
                    if (
                        buttonIndex === navigationList.length - 1 &&
                        navigationList[navigationList.length - 2] &&
                        !navigationList[navigationList.length - 2].showButton
                    ) {
                        showTruncation = true;
                    }

                    return navigationButton.showButton ? (
                        <Fragment key={navigationButton.startRank}>
                            { showTruncation ?
                                <li className="pagination__list-item">
                                    <span className="pagination__text-link body-size-lg" aria-hidden="true">&hellip;</span>
                                </li>
                            : null}
                            <li className="pagination__list-item" itemProp="itemListElement" itemType="http://schema.org/ListItem">
                                <button
                                    className={`pagination__text-link${/*
                                            */' '}body-size-lg${/*
                                            */' '}text-link__underline${/*
                                            */' '}text-link__underline--alt${/*
                                            */' '}${navigationButton.isCurrentPage ? 'text-link__underline--active' : ''}`}
                                    data-startrank={navigationButton.startRank}
                                    itemProp="item"
                                    onClick={onPaginationButtonClick}
                                    ><span className="sr-only">Results page </span>{navigationButton.label}</button>
                                <meta itemProp="position" content={navigationButton.label} />
                            </li>
                        </Fragment>
                    ) : null
                })}

                { paginationList.showNextPageLink ? (
                    <li className="pagination__list-item" itemProp="itemListElement" itemType="http://schema.org/ListItem">
                        <button
                            className="pagination__icon-link icon-next"
                            data-startrank={resultsSummary.nextStart}
                            itemProp="item"
                            onClick={onPaginationButtonClick}
                            role="button"
                            >
                            <span className="sr-only">Go to next page</span>
                            <svg className=""><use xlinkHref="#chevron-down"></use></svg>
                        </button>
                        <meta itemProp="position" content={resultsSummary.nextStart?.toString()} />
                    </li>
                ) : null }

                <meta itemProp="numberOfItems" content={resultsSummary.totalMatching.toString()} />
            </ol>
        </nav>
    );
}
